import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  input,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HtmlEditorComponent } from '../html-editor/html-editor.component';
import { TruncatePipe } from 'src/app/pipe/truncate.pipe';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { noteService, NoteAttachment } from 'src/app/services/note-service';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-item',
  standalone: true,
  imports: [
    CommonModule,
    HtmlEditorComponent,
    TruncatePipe,
    CustomDatePipe,
    MaterialModule,
    TablerIconsModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './item.component.html',
  styleUrl: './item.component.scss',
})
export class ItemComponent implements AfterViewInit {
  @Input() note: any;
  @Input() isEditing: boolean = false;
  @Input() index: number;
  @Input() quillModule: any;
  @Input() role: 'USER' | 'ADMIN' = 'USER';
  hideActions = input<boolean>(false);
  focusMode = input<boolean>(false);
  @Output() onEdit = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<number>();
  @Output() onSave = new EventEmitter<{
    id: any;
    content: string;
    attachments?: NoteAttachment[];
    removeAttachmentIds?: number[];
    newFiles?: File[];
  }>();
  @Output() onTogglePin = new EventEmitter<number>();

  @ViewChild('editFileInput') editFileInput!: ElementRef<HTMLInputElement>;

  notesContent = new FormControl();
  expandedNotes = new Set<number>();
  isExpanded: boolean = false;
  isTruncated: boolean = false;
  truncatedContent: SafeHtml = '';
  fullContent: SafeHtml = '';

  // Attachments for editing
  editingAttachments: NoteAttachment[] = [];
  removedAttachmentIds: number[] = [];
  pendingFiles: File[] = [];
  loadingAttachment: string | null = null;

  // File upload config
  maxFileSize = 10 * 1024 * 1024; // 10MB
  allowedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ];

  @ViewChild('contentElement') contentElement!: ElementRef;

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private noteService: noteService,
    private snackBar: MatSnackBar,
  ) {}

  ngAfterViewInit(): void {
    this.notesContent.setValue(this.note.content);
    this.processContent();
  }

  ngOnChanges(): void {
    if (this.note) {
      this.processContent();
    }
  }

  private processContent(): void {
    const content = this.note.content || this.note;
    this.fullContent = this.sanitizer.sanitize(1, content) || content;
    const truncated = this.getTruncatedContent(content);
    this.truncatedContent =
      this.sanitizer.sanitize(1, truncated.html) || truncated.html;
    this.isTruncated = truncated.isTruncated;
    this.cdr.detectChanges();
  }

  private getTruncatedContent(htmlContent: string): {
    html: string;
    isTruncated: boolean;
  } {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    const blockElements = tempDiv.querySelectorAll(
      'p, div, h1, h2, h3, h4, h5, h6, ul, ol, blockquote',
    );

    let resultHtml = '';
    let lineCount = 0;
    let isTruncated = false;

    if (blockElements.length === 0) {
      const textContent = tempDiv.textContent || '';
      const words = textContent.split(' ');
      const wordsPerLine = 15;
      const maxWords = wordsPerLine * 2;

      if (words.length > maxWords) {
        resultHtml = words.slice(0, maxWords).join(' ') + '...';
        isTruncated = true;
      } else {
        resultHtml = htmlContent;
      }
    } else {
      for (let i = 0; i < blockElements.length; i++) {
        const element = blockElements[i];

        if (lineCount >= 2) {
          isTruncated = true;
          break;
        }

        const clonedElement = element.cloneNode(true) as HTMLElement;

        if (lineCount === 1) {
          const text = clonedElement.textContent || '';
          const words = text.split(' ');
          const maxWordsForSecondLine = 15;
          if (words.length > maxWordsForSecondLine) {
            this.truncateElement(clonedElement, maxWordsForSecondLine);
            isTruncated = true;
          }
        }

        resultHtml += clonedElement.outerHTML;
        lineCount++;
      }

      if (blockElements.length > 2) {
        isTruncated = true;
      }
    }

    return { html: resultHtml, isTruncated };
  }

  private truncateElement(element: HTMLElement, maxWords: number): void {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
    );

    let wordCount = 0;
    let nodesToRemove: Node[] = [];
    let truncateInNode = false;

    while (walker.nextNode()) {
      const textNode = walker.currentNode;
      const words = (textNode.textContent || '')
        .split(' ')
        .filter((w) => w.length > 0);

      if (wordCount + words.length > maxWords) {
        const wordsToInclude = maxWords - wordCount;
        if (wordsToInclude > 0) {
          textNode.textContent =
            words.slice(0, wordsToInclude).join(' ') + '...';
        } else {
          nodesToRemove.push(textNode);
        }
        truncateInNode = true;
      } else if (truncateInNode) {
        nodesToRemove.push(textNode);
      }

      wordCount += words.length;
    }

    nodesToRemove.forEach((node) => {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

  startEdit(): void {
    this.isEditing = true;
    this.editingAttachments = this.note.attachments
      ? [...this.note.attachments]
      : [];
    this.removedAttachmentIds = [];
    this.pendingFiles = [];
    this.onEdit.emit(this.note);
  }

  saveEdit(): void {
    if (this.notesContent.value) {
      this.onSave.emit({
        id: this.note.id,
        content: this.notesContent.value,
        attachments: this.editingAttachments,
        removeAttachmentIds: this.removedAttachmentIds,
        newFiles: this.pendingFiles,
      });
      this.note.content = this.notesContent.value;
      this.processContent();
    }
    this.isEditing = false;
    this.editingAttachments = [];
    this.removedAttachmentIds = [];
    this.pendingFiles = [];
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.notesContent.setValue(this.note.content);
    this.editingAttachments = [];
    this.removedAttachmentIds = [];
    this.pendingFiles = [];
    this.onEdit.emit(false);
  }

  deleteNote(note): void {
    this.onDelete.emit(note);
  }

  togglePin(note: any) {
    this.onTogglePin.emit(note);
  }

  // ========== File Upload Methods ==========

  triggerFileInput(): void {
    this.editFileInput?.nativeElement?.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    const validFiles: File[] = [];

    for (const file of files) {
      if (file.size > this.maxFileSize) {
        this.snackBar.open(
          `File "${file.name}" exceeds 10MB limit`,
          'Close',
          { duration: 3000 },
        );
        continue;
      }
      if (!this.allowedFileTypes.includes(file.type)) {
        this.snackBar.open(
          `File type "${file.type}" not allowed`,
          'Close',
          { duration: 3000 },
        );
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      input.value = '';
      return;
    }

    const totalCount =
      this.editingAttachments.length + this.pendingFiles.length;
    if (totalCount + validFiles.length > 5) {
      this.snackBar.open(
        'Maximum 5 attachments allowed per note',
        'Close',
        { duration: 3000 },
      );
      input.value = '';
      return;
    }

    this.pendingFiles.push(...validFiles);
    this.snackBar.open(
      `${validFiles.length} file(s) added`,
      'Close',
      { duration: 2000 },
    );

    input.value = '';
  }

  removePendingFile(index: number): void {
    this.pendingFiles.splice(index, 1);
  }

  getTotalAttachmentCount(): number {
    return this.editingAttachments.length + this.pendingFiles.length;
  }

  canAddMoreFiles(): boolean {
    return this.getTotalAttachmentCount() < 5;
  }

  // ========== Attachment Display Methods ==========

  getFileIcon(type?: string): string {
    if (!type) return 'insert_drive_file';
    if (type.startsWith('image/')) return 'image';
    if (type === 'application/pdf') return 'picture_as_pdf';
    if (type.includes('word')) return 'description';
    if (type.includes('excel') || type.includes('spreadsheet'))
      return 'table_chart';
    return 'insert_drive_file';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async openAttachment(attachment: NoteAttachment): Promise<void> {
    if (this.loadingAttachment === attachment.cloud_key) return;

    this.loadingAttachment = attachment.cloud_key;

    try {
      const folder = `note-attachments/${this.note.company_id}`;
      const result = await firstValueFrom(
        this.noteService.getAttachmentUrl(attachment.cloud_key, folder),
      );

      if (result?.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to open attachment:', error);
    } finally {
      this.loadingAttachment = null;
    }
  }

  removeEditingAttachment(index: number): void {
    const attachment = this.editingAttachments[index];
    if (attachment.id) {
      this.removedAttachmentIds.push(attachment.id);
    }
    this.editingAttachments.splice(index, 1);
  }
}